import { useCallback, useEffect, useMemo, useRef, useState, type TouchEvent as ReactTouchEvent, type MouseEvent as ReactMouseEvent } from "react";
import css from "./DateSelector.module.css";
import {
  addDays,
  addMonths,
  fmtDayLabel,
  fmtIsoDate,
  fmtMonthHeader,
  fmtRangeLabel,
  getMonthMatrix,
  inRange,
  isWeekend,
  parseIsoDate,
  sameDay,
  startOfDay,
  toIsoWeekday,
  WEEKDAYS_ISO,
} from "./dateUtils";

/**
 * M.1 DateSelector — universal date picker for every role.
 *
 * Two modes — host-configured, not user-toggled:
 *  - "day"   single click selects + closes popover
 *  - "range" first click = start, second = end (auto-swap if end < start)
 *
 * Role-coloured via `var(--role)`; switch by setting <html data-role="…">.
 *
 * External imperative API is published on `window.DateSelector` so the
 * host page can drive the picker programmatically (e.g. preset chips
 * outside the trigger).
 */

export type DateSelectorMode = "day" | "range";

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

export interface DateSelectorApi {
  setMode(mode: DateSelectorMode): void;
  setDate(date: Date): void;
  setRange(start: Date, end: Date): void;
  /** Returns Date in day-mode, {start, end} in range-mode. Null/empties for "not chosen yet". */
  get(): Date | DateRange | null;
  clear(): void;
  open(): void;
  close(): void;
}

interface DateSelectorProps {
  /** Initial mode — defaults to "day". */
  mode?: DateSelectorMode;
  /** Initial selection. */
  initialDate?: Date;
  initialRange?: DateRange;
  /** Called whenever selection changes (after pick or chevron press). */
  onChange?: (value: Date | DateRange) => void;
  /**
   * Register the imperative API on `window.DateSelector`. Default true.
   * Set false if you mount multiple instances and only one should own the global.
   */
  publishApi?: boolean;
}

declare global {
  interface Window {
    DateSelector?: DateSelectorApi;
  }
}

export function DateSelector({
  mode: initialMode = "day",
  initialDate,
  initialRange,
  onChange,
  publishApi = true,
}: DateSelectorProps) {
  const [mode, setMode] = useState<DateSelectorMode>(initialMode);
  const [date, setDate] = useState<Date>(() => initialDate ?? startOfDay(new Date()));
  const [range, setRange] = useState<DateRange>(
    () => initialRange ?? { start: null, end: null },
  );
  const [viewMonth, setViewMonth] = useState<Date>(() => new Date(date.getFullYear(), date.getMonth(), 1));
  const [open, setOpen] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  /* ── Imperative API ──────────────────────────────────────────────────── */

  const api = useMemo<DateSelectorApi>(
    () => ({
      setMode: (m) => setMode(m),
      setDate: (d) => {
        const day = startOfDay(d);
        setDate(day);
        setViewMonth(new Date(day.getFullYear(), day.getMonth(), 1));
        onChangeRef.current?.(day);
      },
      setRange: (s, e) => {
        const start = startOfDay(s);
        const end = startOfDay(e);
        const [a, b] = start.getTime() <= end.getTime() ? [start, end] : [end, start];
        const r: DateRange = { start: a, end: b };
        setRange(r);
        setViewMonth(new Date(a.getFullYear(), a.getMonth(), 1));
        onChangeRef.current?.(r);
      },
      get: () => (mode === "day" ? date : range),
      clear: () => {
        setRange({ start: null, end: null });
      },
      open: () => setOpen(true),
      close: () => setOpen(false),
    }),
    [mode, date, range],
  );

  useEffect(() => {
    if (!publishApi) return;
    window.DateSelector = api;
    return () => {
      if (window.DateSelector === api) delete window.DateSelector;
    };
  }, [api, publishApi]);

  /* ── Outside-click closes the popover ────────────────────────────────── */

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  /* ── Trigger handlers ────────────────────────────────────────────────── */

  const goPrev = useCallback(() => {
    if (mode === "day") {
      const next = addDays(date, -1);
      setDate(next);
      setViewMonth(new Date(next.getFullYear(), next.getMonth(), 1));
      onChangeRef.current?.(next);
    } else if (range.start && range.end) {
      const next: DateRange = { start: addDays(range.start, -1), end: addDays(range.end, -1) };
      setRange(next);
      onChangeRef.current?.(next);
    }
  }, [mode, date, range]);

  const goNext = useCallback(() => {
    if (mode === "day") {
      const next = addDays(date, 1);
      setDate(next);
      setViewMonth(new Date(next.getFullYear(), next.getMonth(), 1));
      onChangeRef.current?.(next);
    } else if (range.start && range.end) {
      const next: DateRange = { start: addDays(range.start, 1), end: addDays(range.end, 1) };
      setRange(next);
      onChangeRef.current?.(next);
    }
  }, [mode, date, range]);

  /* ── Pick a day from the popover ─────────────────────────────────────── */

  const pick = useCallback(
    (d: Date) => {
      const day = startOfDay(d);
      if (mode === "day") {
        setDate(day);
        setOpen(false);
        onChangeRef.current?.(day);
        return;
      }
      // range mode
      if (!range.start || (range.start && range.end)) {
        // start a new range
        setRange({ start: day, end: null });
        onChangeRef.current?.({ start: day, end: null });
        return;
      }
      // we have a start, no end yet → close the range
      const [a, b] =
        day.getTime() < range.start.getTime() ? [day, range.start] : [range.start, day];
      const next = { start: a, end: b };
      setRange(next);
      onChangeRef.current?.(next);
      setOpen(false);
    },
    [mode, range],
  );

  /* ── Render bits ────────────────────────────────────────────────────── */

  const triggerLabel =
    mode === "day"
      ? fmtDayLabel(date)
      : { date: fmtRangeLabel(range.start, range.end) || fmtDayLabel(date).date, weekday: "" };

  return (
    <div ref={rootRef} className={css.root}>
      <div className={css.trigger}>
        <span className={`${css.dtCorner} ${css.tl}`} aria-hidden="true" />
        <span className={`${css.dtCorner} ${css.br}`} aria-hidden="true" />
        <button
          type="button"
          className={css.chev}
          onClick={(e) => {
            e.stopPropagation();
            goPrev();
          }}
          aria-label="−1 день"
        >
          ‹
        </button>
        <span className={css.sep} aria-hidden="true" />
        <button
          type="button"
          className={css.main}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          <span className={css.lbl}>
            <span>{triggerLabel.date}</span>
            {triggerLabel.weekday ? (
              <>
                <span className={css.sepDot} aria-hidden="true">·</span>
                <span className={css.weekday}>{triggerLabel.weekday}</span>
              </>
            ) : null}
          </span>
        </button>
        <span className={css.sep} aria-hidden="true" />
        <button
          type="button"
          className={css.chev}
          onClick={(e) => {
            e.stopPropagation();
            goNext();
          }}
          aria-label="+1 день"
        >
          ›
        </button>
      </div>

      {open ? (
        <Popover
          viewMonth={viewMonth}
          onChangeMonth={setViewMonth}
          mode={mode}
          date={date}
          range={range}
          onPick={pick}
        />
      ) : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────
 * Popover — month-nav + weekday-header + 6×7 day grid.
 * Stage 1c implements the core; Month-Year picker (M.1.4) is wired as a
 * follow-up if/when long jumps become necessary.
 * ────────────────────────────────────────────────────────────────────── */

interface PopoverProps {
  viewMonth: Date;
  onChangeMonth: (next: Date) => void;
  mode: DateSelectorMode;
  date: Date;
  range: DateRange;
  onPick: (d: Date) => void;
}

function Popover({ viewMonth, onChangeMonth, mode, date, range, onPick }: PopoverProps) {
  const today = startOfDay(new Date());
  const cells = useMemo(() => getMonthMatrix(viewMonth), [viewMonth]);

  /* Swipe left/right anywhere in the popover → prev/next month. A swipe also
     suppresses the day-tap it would otherwise trigger. */
  const touchStart = useRef<{ x: number; y: number } | null>(null);
  const swiped = useRef(false);
  const onTouchStart = (e: ReactTouchEvent) => {
    const t = e.touches[0];
    touchStart.current = t ? { x: t.clientX, y: t.clientY } : null;
    swiped.current = false;
  };
  const onTouchEnd = (e: ReactTouchEvent) => {
    const s = touchStart.current;
    const t = e.changedTouches[0];
    if (!s || !t) return;
    const dx = t.clientX - s.x;
    const dy = t.clientY - s.y;
    if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.3) {
      swiped.current = true;
      onChangeMonth(addMonths(viewMonth, dx < 0 ? 1 : -1));
      window.setTimeout(() => { swiped.current = false; }, 320);
    }
  };
  const onGridClickCapture = (e: ReactMouseEvent) => {
    if (swiped.current) { e.stopPropagation(); e.preventDefault(); }
  };

  const isSelected = (d: Date) => {
    if (mode === "day") return sameDay(d, date);
    if (range.start && sameDay(d, range.start)) return true;
    if (range.end && sameDay(d, range.end)) return true;
    return false;
  };

  const isMiddle = (d: Date) => {
    if (mode !== "range" || !range.start || !range.end) return false;
    if (sameDay(d, range.start) || sameDay(d, range.end)) return false;
    return inRange(d, range.start, range.end);
  };

  return (
    <div className={css.popover} role="dialog" aria-label="Выбор даты" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <span className={`${css.popCorner} ${css.popTl}`} aria-hidden="true" />
      <span className={`${css.popCorner} ${css.popTr}`} aria-hidden="true" />
      <span className={`${css.popCorner} ${css.popBl}`} aria-hidden="true" />
      <span className={`${css.popCorner} ${css.popBr}`} aria-hidden="true" />

      <div className={css.monthNav}>
        <button
          type="button"
          className={css.monthChev}
          onClick={() => onChangeMonth(addMonths(viewMonth, -1))}
          aria-label="Предыдущий месяц"
        >
          ‹
        </button>
        <span className={css.monthLabel}>{fmtMonthHeader(viewMonth)}</span>
        <button
          type="button"
          className={css.monthChev}
          onClick={() => onChangeMonth(addMonths(viewMonth, 1))}
          aria-label="Следующий месяц"
        >
          ›
        </button>
      </div>

      <div className={css.weekdayRow} aria-hidden="true">
        {WEEKDAYS_ISO.map((w, i) => (
          <span key={w} className={i >= 5 ? `${css.weekdayCell} ${css.weekend}` : css.weekdayCell}>
            {w}
          </span>
        ))}
      </div>

      <div className={css.grid} role="grid" onClickCapture={onGridClickCapture}>
        {cells.map(({ date: d, inMonth }) => {
          const selected = isSelected(d);
          const middle = isMiddle(d);
          const today_ = sameDay(d, today);
          const weekend = isWeekend(d);
          const cls = [
            css.cell,
            !inMonth && css.cellMuted,
            today_ && css.cellToday,
            selected && css.cellSelected,
            middle && css.cellMiddle,
            weekend && !selected && css.cellWeekend,
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={fmtIsoDate(d) + (inMonth ? "" : "x")}
              type="button"
              role="gridcell"
              className={cls}
              onClick={() => onPick(d)}
              aria-current={today_ ? "date" : undefined}
              aria-selected={selected}
              data-iso={fmtIsoDate(d)}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* Re-export helpers so consumers don't have to reach into dateUtils. */
export { parseIsoDate, fmtIsoDate, fmtDayLabel } from "./dateUtils";
export { toIsoWeekday };
